import { toast } from 'sonner';

import { useDeleteJobNoteMutation } from '@/modules/jobs/mutations/useDeleteJobNoteMutation';
import { useUpdateJobNoteMutation } from '@/modules/jobs/mutations/useUpdateJobNoteMutation';
import { NoteCard } from '@/modules/notes/components/NoteCard';
import type {
  JobNoteType,
  NoteFormData,
} from '@/modules/notes/components/NoteForm';
import type { components } from '@/types/apiSchema';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { jobNoteTypeOptions } from './JobNotesList';

type JobNoteResponse = components['schemas']['JobNoteResponse'];

interface JobNoteCardProps {
  note: JobNoteResponse;
  jobId: string;
}

export const JobNoteCard = ({ note, jobId }: JobNoteCardProps) => {
  const deleteNoteMutation = useDeleteJobNoteMutation();
  const updateNoteMutation = useUpdateJobNoteMutation();

  const handleUpdate = (data: NoteFormData) => {
    updateNoteMutation.mutate(
      {
        jobId,
        noteId: note.id,
        body: {
          content: data.content,
          type: data.type as JobNoteType,
        },
      },
      {
        onSuccess: () => {
          toast.success('Note updated successfully');
        },
        onError: (error) => {
          toast.error(`Failed to update note: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  const handleDelete = (noteId: string) => {
    deleteNoteMutation.mutate(
      { jobId, noteId },
      {
        onSuccess: () => {
          toast.success('Note deleted successfully');
        },
        onError: (error) => {
          toast.error(`Failed to delete note: ${extractErrorMessage(error)}`);
        },
      },
    );
  };

  return (
    <NoteCard
      note={note}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isUpdating={updateNoteMutation.isPending}
      typeOptions={jobNoteTypeOptions}
    />
  );
};
